import { memo, useCallback, useState } from 'react';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { getExcelFileName } from 'src/utils/getExcelFileName.ts';
import { DownloadIcon } from 'src/modules/common/icons/DownloadIcon.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { Button } from 'src/modules/common/components/Button/Button.tsx';
import { UserSettings } from 'src/types/UserSettings';
import { SelectionRepository } from 'src/projects/SelectionRepository.ts';
import { saveAs } from 'file-saver';
import { composeExcelBuffer } from 'src/projects/hooks/composeExcelBuffer.ts';
import { useBelimoTranslate } from 'src/common/hooks/useBelimoTranslate.ts';
import { exportAllPrepare } from 'src/projects/hooks/exportAllPrepare.ts';

type Props = {
    selectionRepository: SelectionRepository;
    userSettings: UserSettings;
}

export const ExportExcelButton = memo(({
    selectionRepository,
    userSettings,
}: Props) => {
    const [inProgress, setInProgress] = useState<boolean>(false);
    const t = useBelimoTranslate();
    const onClick = useCallback(async () => {
        if (inProgress) {
            return;
        }
        setInProgress(true);
        const data = await exportAllPrepare({
            selectionRepository,
            userSettings,
            t,
        });
        const buffer = await composeExcelBuffer(data);
        saveAs(new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }), getExcelFileName());
        setInProgress(false);
    }, [inProgress, selectionRepository, userSettings, t]);
    return (
        <Button
            type="button"
            kind="secondary"
            as="a"
            onClick={onClick}
            loading={inProgress}
            icon={(
                <div className="vss-project-page__icon">
                    <DownloadIcon/>
                </div>
            )}
        >
            <I18n messageId={MESSAGES.projectPageDownloadExcel}/>
        </Button>
    );
});
